// useGoogleAdsTag — loads Google's gtag.js for the Ads conversion tag on the
// marketing site, and only once the visitor has actively consented.
//
// Two gates, both required:
//
//   1. isMarketingHost() — the tag belongs on stemne.no. A tenant's booking
//      page is the operator's surface, not Stemne's ad funnel; injecting a
//      third-party tracker there would fold every operator's customers into
//      our remarketing audience without anyone having asked them.
//   2. consent === 'granted' — 'unset' and 'denied' both mean don't load.
//      Consent Mode's default-deny (useCookieConsent) already means a
//      loaded-but-unconsented tag records nothing, but not loading it at all
//      is the stronger promise: zero requests reach Google before yes.
//
// The conversion action ("Sign-up") lives in the Google Ads account. It was
// created as WEBPAGE_CODELESS on the assumption that a page-load rule against
// stemne.no/get-started/subdomain would fire off the base tag's own page_view,
// so this file used to carry NO gtag('event', 'conversion', …) call, on the
// stated reasoning that adding one "would double-count".
//
// That was wrong, and silently so for ~90 days (2026-09-07). The account's
// `metrics.conversion_last_received_request_date_time` for the Sign-up action
// reads 1970-01-01 — the epoch, i.e. Google has NEVER received a single tag
// request for it. Verified against a real consented prod signup that completed
// through /get-started/subdomain and still moved nothing. Whatever page-load
// rule was supposed to exist either was never configured or never matched.
// There is nothing to double-count, because the codeless side has never fired.
//
// So the conversion is now reported explicitly, using Google's own event
// snippet for this action. Rules that keep it honest:
//   - Consent is re-checked AT FIRE TIME, not just at inject time. Queuing a
//     conversion into dataLayer while consent is 'denied' would sit there and
//     replay the moment the visitor later says yes — a consent violation with
//     a delay fuse.
//   - It fires at most once per browser (SIGNUP_FIRED_KEY). An operator signs
//     up once; a reload of the claim step is not a second conversion.
//   - A configured tag with no label is a LOUD failure, not a silent one. That
//     asymmetry is the whole lesson of the 90 days above.
//
// Empty VITE_GOOGLE_ADS_CONVERSION_ID disables the whole thing (dev / CI
// path), mirroring how useTurnstile treats an empty site key.
//
// One deliberate difference from useTurnstile's loadScript(): no polling.
// Turnstile has to wait for window.turnstile before it can render a widget.
// gtag.js attaches no global we need — the shim in useCookieConsent queues
// commands onto window.dataLayer and the script replays that queue when it
// arrives. So inject-and-queue is the entire job. The part worth copying is
// the double-injection guard.

import * as Sentry from '@sentry/vue'
import { watch } from 'vue'
import { pushGtag, useCookieConsent } from '@/composables/useCookieConsent'
import { isMarketingHost } from '@/utils/platformHost'

const SCRIPT_ID = 'google-ads-gtag'
const TAG_ID = (import.meta.env.VITE_GOOGLE_ADS_CONVERSION_ID as string | undefined) ?? ''

// The conversion action's label, the second half of gtag's `send_to`
// ("AW-XXXXXXX/<label>"). Kept in env next to the tag id rather than inlined,
// because the pair identifies one conversion action in one Ads account and a
// staging account would need a different one.
const CONVERSION_LABEL =
  (import.meta.env.VITE_GOOGLE_ADS_CONVERSION_LABEL as string | undefined) ?? ''

// One conversion per browser. localStorage, not sessionStorage: a signup that
// spans a verification email and a fresh tab is still one signup.
const SIGNUP_FIRED_KEY = 'stemne.gads.signupConversionFired'

// Module-scoped so neither a second useGoogleAdsTag() call nor a consent ref
// that churns (granted → denied → granted) can inject a second <script>.
// The DOM check covers a tag that survived from a prior module instance.
let injected = false

function injectTag(tagId: string): void {
  if (injected) return
  injected = true
  if (!document.getElementById(SCRIPT_ID)) {
    const s = document.createElement('script')
    s.id = SCRIPT_ID
    s.async = true
    s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(tagId)}`
    document.head.appendChild(s)
  }
  pushGtag('js', new Date())
  pushGtag('config', tagId)
}

function alreadyFired(): boolean {
  try {
    return localStorage.getItem(SIGNUP_FIRED_KEY) === '1'
  } catch {
    // Private mode / storage blocked. Degrade toward sending: Google dedupes
    // its own conversions far better than we can guess here, and a missing
    // conversion is the failure mode we are actually trying to fix.
    return false
  }
}

function markFired(): void {
  try {
    localStorage.setItem(SIGNUP_FIRED_KEY, '1')
  } catch {
    // Nothing to do — the conversion still went out, which is the point.
  }
}

/**
 * reportSignupConversion — tell Google Ads a signup completed.
 *
 * Call this when the operator has actually reached the end of signup, not on
 * every render of the page it lives on. Safe to call more than once; only the
 * first call in a browser sends anything.
 *
 * Returns true when a conversion was sent, so callers and tests can assert on
 * it rather than reaching into window.dataLayer.
 */
export function reportSignupConversion(): boolean {
  if (!TAG_ID || !isMarketingHost()) return false

  if (!CONVERSION_LABEL) {
    // Loud on purpose. The silent version of this branch cost ~90 days of
    // zero conversion data while every dashboard looked correctly configured.
    const message =
      '[google-ads] VITE_GOOGLE_ADS_CONVERSION_LABEL is empty while ' +
      'VITE_GOOGLE_ADS_CONVERSION_ID is set — signup conversions are NOT ' +
      'being reported to Google Ads.'
    console.error(message)
    // A console line in a visitor's browser is not meaningfully louder than
    // silence — nobody is watching it. Sentry is what makes this reach a
    // human, which is the entire difference between this and the 90 days of
    // nothing that preceded it.
    Sentry.captureMessage(message, 'error')
    return false
  }

  // Consent is re-read here, not inherited from injection time. A visitor can
  // decline after the tag loaded, and a queued conversion must not survive it.
  const { consent } = useCookieConsent()
  if (consent.value !== 'granted') return false

  if (alreadyFired()) return false
  markFired()

  pushGtag('event', 'conversion', {
    send_to: `${TAG_ID}/${CONVERSION_LABEL}`,
    value: 1.0,
    currency: 'NOK',
  })
  return true
}

export function useGoogleAdsTag(): void {
  if (!TAG_ID || !isMarketingHost()) return
  const { consent } = useCookieConsent()
  // No stop handle: this is called once from the app root and must outlive
  // every route change, so the watcher's lifetime is the app's lifetime.
  watch(
    consent,
    (value) => {
      if (value === 'granted') injectTag(TAG_ID)
    },
    { immediate: true },
  )
}
